import { m, useInView } from "framer-motion";
import { useRef } from "react";

interface MotionProps {
    children: React.ReactNode;
    className?: string;
    delay?: number;
    duration?: number;
}

export const FadeIn = ({ children, className = "", delay = 0, duration = 0.5 }: MotionProps) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    return (
        <m.div
            ref={ref}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration, delay, ease: "easeOut" }}
            style={{ willChange: "opacity" }}
            className={className}
        >
            {children}
        </m.div>
    );
};

export const SlideUp = ({ children, className = "", delay = 0, duration = 0.5 }: MotionProps) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    return (
        <m.div
            ref={ref}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration, delay, ease: "easeOut" }}
            style={{ willChange: "opacity, transform" }}
            className={className}
        >
            {children}
        </m.div>
    );
};

export const ScaleIn = ({ children, className = "", delay = 0, duration = 0.5 }: MotionProps) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    return (
        <m.div
            ref={ref}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration, delay, ease: "easeOut" }}
            style={{ willChange: "opacity, transform" }}
            className={className}
        >
            {children}
        </m.div>
    );
};

export const StaggerContainer = ({ children, className = "", delay = 0, staggerDelay = 0.1 }: { children: React.ReactNode, className?: string, delay?: number, staggerDelay?: number }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    return (
        <m.div
            ref={ref}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={{
                visible: {
                    transition: {
                        staggerChildren: staggerDelay,
                        delayChildren: delay
                    }
                },
                hidden: {}
            }}
            className={className}
        >
            {children}
        </m.div>
    );
};

export const StaggerItem = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => {
    return (
        <m.div
            variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.5 }}
            style={{ willChange: "opacity, transform" }}
            className={className}
        >
            {children}
        </m.div>
    );
};
